import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Button, Spinner } from 'react-bootstrap';
import { Image as ImageIcon, Trash, X } from 'react-bootstrap-icons';
import { toast } from 'react-toastify';
import { makeApiRequests } from '../../helpers/api';
import { ENDPOINTS } from '../../helpers/constants';
import { getImageUrl } from '../../helpers/imageHelpers';

const UnitCoverImage = ({ inventory, onUpdated }) => {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);

  const cover = inventory?.coverImage;
  // The slot card paints this at roughly 600×168, so request a display-sized file.
  const imageUrl = cover?.url ? getImageUrl(cover.url, { width: 1200 }) : '';
  const fullImageUrl = cover?.url ? getImageUrl(cover.url, { width: 1600 }) : '';

  useEffect(() => {
    if (!previewOpen) return undefined;
    const onKeyDown = event => {
      if (event.key === 'Escape') setPreviewOpen(false);
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [previewOpen]);

  const saveCoverImage = async (coverImage, previousFileName) => {
    const { response, error } = await makeApiRequests({
      endpoint: ENDPOINTS.INVENTORY_WITH_ID(inventory._id),
      method: 'PUT',
      requestBody: { coverImage }
    });

    if (error) {
      toast.error(error);
      if (coverImage?.url && coverImage.url !== previousFileName) {
        makeApiRequests({
          endpoint: ENDPOINTS.DELETE_DISK_IMAGE(coverImage.url),
          method: 'DELETE'
        });
      }
      return null;
    }

    const updatedInventory = {
      ...inventory,
      ...response,
      inventoryStatus: inventory.inventoryStatus,
      coverImage: response?.coverImage ?? coverImage
    };
    if (onUpdated) onUpdated(updatedInventory);
    return updatedInventory;
  };

  const onFileSelected = async event => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file || !inventory?._id || busy) return;

    if (!file.type.startsWith('image/')) {
      toast.error('Only image files are allowed.');
      return;
    }

    setBusy(true);
    const formData = new FormData();
    formData.append('image', file);

    const { error, response } = await makeApiRequests({
      requestBody: formData,
      endpoint: ENDPOINTS.DISK_IMAGE_UPLOAD,
      method: 'POST',
      stringify: false
    });

    if (error || !response?.filename) {
      setBusy(false);
      toast.error(error || 'Unable to upload the cover image.');
      return;
    }

    const previousFileName = cover?.url || '';
    const saved = await saveCoverImage(
      { url: response.filename, uploadedAt: new Date().toISOString() },
      previousFileName
    );
    setBusy(false);

    if (saved) {
      toast.success(previousFileName ? 'Cover image replaced.' : 'Cover image added.');
    }
  };

  const deleteCoverImage = async () => {
    if (!cover?.url || busy) return;
    setBusy(true);
    const saved = await saveCoverImage(null, cover.url);
    setBusy(false);
    setConfirmDelete(false);
    setPreviewOpen(false);
    if (saved) toast.success('Cover image removed.');
  };

  return (
    <div className="mb-2" onClick={event => event.stopPropagation()}>
      <div
        style={{
          position: 'relative',
          width: '100%',
          height: 168,
          borderRadius: 16,
          overflow: 'hidden',
          background: '#f7f2f4',
          border: imageUrl ? '1px solid #eadfe3' : '1px dashed #c9b3bb'
        }}
      >
        {imageUrl ? (
          <button
            type="button"
            onClick={() => setPreviewOpen(true)}
            title="View full size"
            style={{
              display: 'block',
              width: '100%',
              height: '100%',
              padding: 0,
              border: 0,
              background: 'transparent',
              cursor: 'zoom-in'
            }}
          >
            <img
              src={imageUrl}
              alt="Unit cover"
              loading="lazy"
              decoding="async"
              width="580"
              height="168"
              style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
            />
          </button>
        ) : (
          <div className="h-100 d-flex flex-column align-items-center justify-content-center text-muted">
            <ImageIcon size={48} />
            <span className="smallFont mt-1">No cover image</span>
          </div>
        )}

        <div
          className="d-flex"
          style={{ position: 'absolute', right: 8, bottom: 8, gap: 6 }}
        >
          <Button
            type="button"
            size="sm"
            variant={imageUrl ? 'light' : 'primary'}
            className="py-0 px-2"
            style={{ fontSize: '0.75rem' }}
            disabled={busy || !inventory?._id}
            onClick={() => inputRef.current?.click()}
          >
            {imageUrl ? 'Replace' : 'Add'}
          </Button>
          {imageUrl && (
            <Button
              type="button"
              size="sm"
              variant="danger"
              className="py-0 px-2 d-inline-flex align-items-center"
              style={{ fontSize: '0.75rem' }}
              disabled={busy}
              onClick={() => setConfirmDelete(true)}
            >
              <Trash size={12} className="mr-1" />
              Delete
            </Button>
          )}
        </div>

        {busy && (
          <div
            className="d-flex align-items-center justify-content-center"
            style={{
              position: 'absolute',
              inset: 0,
              background: 'rgba(255,255,255,0.72)'
            }}
          >
            <Spinner animation="border" size="sm" />
          </div>
        )}
      </div>

      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        style={{ display: 'none' }}
        onChange={onFileSelected}
      />

      {previewOpen &&
        imageUrl &&
        createPortal(
          <div
            role="dialog"
            aria-modal="true"
            aria-label="Cover image"
            onClick={() => setPreviewOpen(false)}
            style={{
              position: 'fixed',
              inset: 0,
              zIndex: 20000,
              background: 'rgba(0,0,0,0.92)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: 24
            }}
          >
            <Button
              type="button"
              variant="light"
              size="sm"
              aria-label="Close"
              onClick={() => setPreviewOpen(false)}
              style={{ position: 'absolute', top: 16, right: 16 }}
            >
              <X size={18} />
            </Button>
            <img
              src={fullImageUrl}
              alt="Unit cover full size"
              loading="eager"
              decoding="async"
              onClick={event => event.stopPropagation()}
              style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
            />
          </div>,
          document.body
        )}

      {confirmDelete &&
        createPortal(
          <div
            role="dialog"
            aria-modal="true"
            aria-label="Delete cover image"
            onClick={() => {
              if (!busy) setConfirmDelete(false);
            }}
            style={{
              position: 'fixed',
              inset: 0,
              zIndex: 20001,
              background: 'rgba(0,0,0,0.45)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: 16
            }}
          >
            <div
              className="bg-white rounded p-3 shadow"
              style={{ width: '100%', maxWidth: 420 }}
              onClick={event => event.stopPropagation()}
            >
              <h6 className="mb-2">Delete cover image?</h6>
              <p className="small text-muted mb-3">This removes the cover image from the unit.</p>
              <div className="d-flex justify-content-end" style={{ gap: 8 }}>
                <Button
                  type="button"
                  size="sm"
                  variant="secondary"
                  disabled={busy}
                  onClick={() => setConfirmDelete(false)}
                >
                  Cancel
                </Button>
                <Button type="button" size="sm" variant="danger" disabled={busy} onClick={deleteCoverImage}>
                  {busy ? 'Deleting...' : 'Delete'}
                </Button>
              </div>
            </div>
          </div>,
          document.body
        )}
    </div>
  );
};

export default UnitCoverImage;
